/* eslint-disable react/button-has-type */
import React from 'react';
import Rodal from 'rodal';
import { encodeHTML } from 'entities';
import { Cookies } from 'react-cookie-consent';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import toCapitalCase from 'to-capital-case';

// Components
import Button from './components/Button';
import ClipWrapper from './components/ClipWrapper';
import Content from './components/Content';
import CookieBanner from './components/CookieBanner';
import Filter from './components/Filter';
import Menu from './components/Menu';
import Nav from './components/Nav';
import Splash from './components/Splash';
import SvgImage from './components/SvgImage';
import ZoomTools from './components/ZoomTools';

// Helpers
import noop from './helpers/noop';

// Data
import data from './data/data';

// Constants
import {
  BASE_URL,
  SVG_IMAGE_WIDTH,
  SVG_IMAGE_HEIGHT,
  ZOOM_VALUE,
} from './constants';

// Styles
import './App.css';
import './components/shake.css';
import 'rodal/lib/rodal.css'; // Rodal (React Modal with Animations) styles

// Individual modal styles
const maskStyles = {
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  height: '100vh',
  width: '100vw',
  paddingLeft: '0',
};
const contentMaskStyles = {
  backgroundColor: 'transparent',
  height: '100vh',
  width: '100vw',
  paddingLeft: '0',
};
const splashModalStyles = {
  background: `url('${BASE_URL}/images/cocodrilo.jpg') no-repeat 50%`,
  backgroundColor: '#fef1e1',
  backgroundSize: 'cover',
  height: '100vh',
  width: '100vw',
};
const menuModalStyles = {
  background: `url('${BASE_URL}/images/menu-fondo.jpg') no-repeat 50%`,
  backgroundSize: 'cover',
  width: '80vw',
  height: 'auto',
  animationDuration: '300ms',
  top: '15%',
  bottom: '15%',
  boxShadow: '0px 0px 15px 0px rgba(0, 0, 0, .7)',
  padding: '0',
};

const contentModalStyles = {
  background: `url('${BASE_URL}/images/menu-fondo.jpg') no-repeat 50%`,
  backgroundSize: 'cover',
  width: '80vw',
  height: 'auto',
  animationDuration: '300ms',
  top: '15%',
  bottom: '15%',
  padding: '0',
};

const App = class extends React.PureComponent {
  constructor() {
    super();
    this.state = {
      activeClip: data[0], // Load the first one so it will never render empty
      activeContent: null,
      filter: [],
      firstVisitComplete: !!Cookies.get('firstVisitComplete') || false,
      maxScale: 3,
      showClip: false,
      showContent: false,
      showCookieBanner: !Cookies.get('hasRespondedGdpr') || false, // if they haven't responded to the cookie banner, show it
      showMenu: false,
      showCategoryCensura: true,
      showCategoryContexto: true,
      showCategoryFormacion: true,
      showCategoryMercado: true,
      showCategoryObra: true,
    };
    this.openClip = this.openClip.bind(this);
    this.closeClip = this.closeClip.bind(this);
    this.updateClip = this.updateClip.bind(this);
    this.openMenu = this.openMenu.bind(this);
    this.closeMenu = this.closeMenu.bind(this);
    this.openContent = this.openContent.bind(this);
    this.closeContent = this.closeContent.bind(this);
    this.closeAll = this.closeAll.bind(this);
    this.matchParent = this.matchParent.bind(this);
    this.addTransform = this.addTransform.bind(this);
    this.removeTransform = this.removeTransform.bind(this);
    this.moveToFront = this.moveToFront.bind(this);
    this.setFirstVisit = this.setFirstVisit.bind(this);
    this.updateFilter = this.updateFilter.bind(this);
  }

  // Lifecycle Methods
  componentDidUpdate() {
    // When swithcing to a new video, make sure to stop any current video
    // that is playing and then load the new one
    const overlay = document.getElementsByClassName('video-static-overlay')[0];
    const video = document.getElementsByTagName('video')[0];
    let videoPlaying;
    console.log(videoPlaying); // this is only here to avoid a "no unused vars" error 🙄

    if (video) {
      video.load();
      video.pause();
      videoPlaying = false;
    }

    const hideOverlay = () => {
      overlay.style.display = 'none';
      videoPlaying = true;
      video.play();
    };

    const showOverlay = () => {
      // this check is to differentiate seek and actual pause
      if (video.readyState === 4) {
        overlay.style.display = 'block';
        videoPlaying = false;
      }
    };

    overlay.addEventListener('click', hideOverlay);
    video.addEventListener('pause', showOverlay);
  }

  /* 
    ============================================================
                    Non-Lifecycle stuff
    ============================================================
  */
  /**
   * Openclip receives a click event, closes all open modals,
   * and calls updateClip with the ID of the clip to open.
   *
   * @param {Event} e
   */
  openClip = e => {
    this.closeAll();
    const clipId = e.target.id - 1;
    this.setState({ showClip: true });
    document.getElementsByClassName('video-static-overlay')[0].style.display =
      'block';
    this.updateClip(clipId);
  };

  /**
   * Closes Clip modal
   */
  closeClip = () => {
    this.setState({ showClip: false });
  };

  openMenu = () => {
    this.closeAll();
    this.setState({ showMenu: true });
  };

  closeMenu = () => {
    this.setState({ showMenu: false });
  };

  openContent = content => {
    this.closeAll();
    this.setState({ showContent: true, activeContent: content });
  };

  closeContent = () => {
    this.setState({ showContent: false });
  };

  closeAll = () => {
    this.setState({ showClip: false, showContent: false });
  };

  /**
   * matchParent desperately needs some documentation.
   *
   * @param {Element} el
   * @param {String} CLASSNAME
   */
  matchParent = (el, CLASSNAME) => {
    while (el.parentNode) {
      // eslint-disable-next-line no-param-reassign
      el = el.parentNode;
      if (el.className.includes(CLASSNAME)) {
        return el;
      }
    }
    return null;
  };

  /**
   * updateClip receives an event and updates the videoclip modal with the selected videoclip
   *
   * @param {Event} e
   */
  updateClip = e => {
    let clipNumber;
    if (typeof e !== 'number') {
      clipNumber = e.currentTarget.id - 1;
      this.matchParent(e.currentTarget, 'rodal-dialog').scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    } else {
      clipNumber = e;
    }
    this.setState({ activeClip: data[`${clipNumber}`] });
    document.getElementsByClassName('video-static-overlay')[0].style.display =
      'block';
  };

  updateFilter = category => {
    const stateName = `showCategory${category}`;
    this.setState(prevState => ({
      // eslint-disable-next-line security/detect-object-injection
      [stateName]: !prevState[stateName],
    }));
  };

  /**
   * addTransform is intended to replace css
   * transform functionality which can be closely replicated via adding
   * html attributes to SVG internal elements
   * @param {Event} e
   */
  addTransform = e => {
    const { target } = e;
    this.moveToFront(target);
    this.showArtistInfo(target);
    setTimeout(() => {
      target.setAttribute('height', Math.round(SVG_IMAGE_HEIGHT * ZOOM_VALUE));
      target.setAttribute('width', Math.round(SVG_IMAGE_WIDTH * ZOOM_VALUE));
      target.className.baseVal += ' hover';
    }, 1);
  };

  /**
   * removeTransform is intended to replace css transform functionality
   * which can be closely replicated via removing html attributes to
   * SVG internal elements
   * @param {Event} e
   */
  removeTransform = e => {
    const { target } = e;
    const hoverClass = 'hover';
    // eslint-disable-next-line security/detect-non-literal-regexp
    const reg = new RegExp(`(^| )${hoverClass}($| )`, 'g');
    target.className.baseVal = target.className.baseVal.replace(reg, ' ');
    this.removeArtistInfo();
    target.setAttribute('height', SVG_IMAGE_HEIGHT);
    target.setAttribute('width', SVG_IMAGE_WIDTH);
  };

  /**
   * SVG Doesn't support z-index, rather displays items in the order they
   * are appear in the document. To make the hovered element appear on
   * top of other elements we remove the element from it's parent and
   * append the element to the same parent. Takes node, appends it to
   * it's parent, thus making it the most recent element.
   * @param {any} node
   */
  moveToFront = node => {
    node.parentNode.appendChild(node);
  };

  showArtistInfo = target => {
    const artistName = target.getAttribute('data-artist');
    // const category = target.getAttribute('data-category');
    const overlay = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'text'
    );
    const overlayBG = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'text'
    );
    const svg = document.getElementsByTagName('svg')[0];
    const y = parseFloat(target.getAttribute('y'), 10) + 260;
    const x = parseFloat(target.getAttribute('x'), 10) + 10;
    overlay.setAttribute('y', y);
    overlay.setAttribute('x', x);
    overlayBG.setAttribute('y', y);
    overlayBG.setAttribute('x', x);
    overlay.innerHTML = encodeHTML(artistName);
    overlayBG.innerHTML = encodeHTML(artistName);
    overlay.className.baseVal += ' overlay';
    overlayBG.className.baseVal += ' overlayBG';
    svg.appendChild(overlayBG);
    svg.appendChild(overlay);
    setTimeout(() => {
      overlay.style.opacity = 1;
      overlayBG.style.opacity = 1;
    }, 1);
  };

  removeArtistInfo = () => {
    const artistInfo = document.querySelectorAll('svg text')[0];
    if (artistInfo) {
      artistInfo.remove();
      this.removeArtistInfo();
    }
  };

  setFirstVisit = () => {
    Cookies.set('firstVisitComplete', true);
    this.setState({ firstVisitComplete: true });
  };

  render() {
    const {
      activeClip,
      activeContent,
      firstVisitComplete,
      showCategoryCensura,
      showCategoryContexto,
      showCategoryFormacion,
      showCategoryMercado,
      showCategoryObra,
      showClip,
      showContent,
      showCookieBanner,
      showMenu,
    } = this.state;

    // The buttons for "internal" navigation
    const clipNavigationArray = data.filter(
      clip => clip.category === activeClip.category
    );
    const relatedClips = clipNavigationArray.map(clip => {
      const { artist, artistFull, id, images } = clip;
      return (
        <div key={`related-clip-${id}`}>
          <Button
            className={`related-clip-${id} grow`}
            id={id}
            imageAlt={artist}
            imageSrc={images.medium}
            imageWidth={180}
            onClick={this.updateClip}
          />
          <span className='button-text'>{artistFull}</span>
        </div>
      );
    });

    // The buttons for main navigation
    const clips = data.map(clip => {
      const { artist, artistFull, category, id, images, posX, posY } = clip;
      const { state } = this;
      const capitalizedCategory = toCapitalCase(category);
      const visible = state[`showCategory${capitalizedCategory}`];
      return (
        <SvgImage
          artistFull={artistFull}
          category={category}
          className={`map-marker ${artist}-${category}`}
          height={SVG_IMAGE_HEIGHT}
          id={id}
          image={images.medium}
          key={`clip-${id}`}
          onClick={this.openClip}
          onMouseEnter={this.addTransform}
          onMouseLeave={this.removeTransform}
          posX={posX}
          posY={posY}
          width={SVG_IMAGE_WIDTH}
          visible={visible}
        />
      );
    });
    return (
      <div id='app' className='App'>
        <Nav onClick={this.openMenu} />
        <Rodal
          animation='slideDown'
          className='menu-modal'
          closeOnEsc
          customStyles={menuModalStyles}
          customMaskStyles={maskStyles}
          onClose={this.closeMenu}
          visible={showMenu}
        >
          <Menu openContent={this.openContent} />
        </Rodal>
        <Rodal
          animation='slideUp'
          className='content-modal'
          closeOnEsc
          customStyles={contentModalStyles}
          customMaskStyles={contentMaskStyles}
          onClose={this.closeContent}
          visible={showContent}
        >
          {showContent && <Content content={activeContent} />}
        </Rodal>

        <Rodal
          animation='slideUp'
          className='splash-modal'
          closeOnEsc
          customStyles={splashModalStyles}
          onClose={this.setFirstVisit}
          visible={!firstVisitComplete}
        >
          <Splash setFirstVisit={this.setFirstVisit} />
        </Rodal>

        <Rodal
          animation='slideUp'
          className='videoclip-modal'
          closeOnEsc
          customStyles={maskStyles}
          onClose={this.closeClip}
          visible={showClip}
        >
          <ClipWrapper
            artist={activeClip.artist}
            category={activeClip.category}
            images={activeClip.images}
            relatedClips={relatedClips}
            videoSrc={activeClip.video}
          />
        </Rodal>

        <section>
          <TransformWrapper
            defaultScale={1}
            options={{ maxScale: 2 }}
            zoomIn={{ step: 7 }}
            zoomOut={{ step: 7 }}
          >
            {({
              // eslint-disable-next-line no-shadow
              options: { maxScale },
              resetTransform,
              scale,
              zoomIn,
              zoomOut,
              ...rest
            }) => (
              <>
                <ZoomTools
                  noop={noop}
                  scale={scale}
                  zoomIn={zoomIn}
                  zoomOut={zoomOut}
                />

                <TransformComponent>
                  <div className='fondo-pan-zoom'>
                    <svg
                      className='cuba-svg'
                      preserveAspectRatio='xMidYMid slice'
                      viewBox='0 0 4119 2317'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <defs>
                        <filter x='0' y='0' width='1.1' height='0.8' id='solid'>
                          <feFlood floodColor='white' />
                          <feComposite in='SourceGraphic' operator='xor' />
                        </filter>
                      </defs>
                      <g transform='matrix(1.308041, 0, 0, 1.307211, -69.144325, -337.042694)'>
                        <path
                          d='M505.387,1220.14L586.945,1199.83L598.816,1180.4L633.462,1158.92L661.94,1163.51L663.349,1138.82L636.941,1106.2L640.627,1061.5L672.136,1022.48L706.345,1000.76L751.399,987.273L789.635,965.898L799.849,1008.51L811.729,963.385L854.992,932.045L898.614,933.477L934.775,911.635L999.958,933.755L1059.1,908.783L1122.25,895.289L1171.21,887.361L1200.8,886.759L1208,865.522L1237.16,891.545L1301.92,897.805L1350.02,908.848L1381.32,916.259L1402.08,904.291L1422.23,904.047L1472.93,918.344L1474.3,946.019L1571.57,956.8L1574.22,984.831L1600.57,981.958L1629.74,1053.46L1796.52,1108.18L2058.11,1240.36L2098.81,1276.46L2151.7,1300.23L2172.26,1285.51L2220.36,1276.42L2262.3,1302.86L2421.83,1257.75L2416.14,1303.28L2367.61,1339.14L2365.1,1380.18L2419.6,1390.1L2565.49,1370.01L2636.58,1455.54L2709.02,1466.8L2670.55,1499.15L2649,1521.38L2576.23,1519.33L2492.57,1553.54L2438.72,1575.75L2319.02,1554.47L2274.22,1555.29L2138.1,1607.83L1877.84,1640.69L1903.53,1581.09L1960.04,1560.55L2022.17,1507.48L2036.12,1483.18L2000.85,1434.06L1945.23,1434.7L1871.13,1443.97L1790.61,1404.46L1763.33,1328.71L1748.2,1286.33L1711.98,1274.16L1685.76,1286.61L1665.45,1273.06L1630.86,1297.71L1428.23,1242.77L1383.44,1175.68L1358.06,1126.37L1352.84,1165.85L1279.06,1150.63L1259.54,1111.62L1255.14,1151.85L1232.43,1158.96L1120.28,1124.19L1133.16,1099.53L1211.41,1086.9L1187.21,1083.02L1164.05,1061.26L1061.03,1064.21L1013.09,1051.52L1013.34,1074L936.799,1130.16L912.987,1133.8L887.08,1161.81L784.788,1141.78L778.455,1166.56L766.276,1222.39L658.346,1247.51L665.673,1214.35L567.764,1256.71L505.387,1220.14Z'
                          style={{ fill: 'none' }}
                        />
                      </g>
                      {clips}
                    </svg>
                  </div>
                </TransformComponent>
                <Filter
                  censura={showCategoryCensura}
                  contexto={showCategoryContexto}
                  formacion={showCategoryFormacion}
                  mercado={showCategoryMercado}
                  obra={showCategoryObra}
                  updateFilter={this.updateFilter}
                />
              </>
            )}
          </TransformWrapper>
        </section>
        {showCookieBanner && <CookieBanner />}
      </div>
    );
  }
};

export default App;
